/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { CoreAppStore } from '@opengeoweb/store';
import { uiSelectors } from '@opengeoweb/store';
import type { DrawingListItem } from '../../../store/warningsDrawings/types';
import { warningsEventFormActions } from '../../../store/warningsEventForm/reducer';
import { getWarningsEventSelectedDrawing } from '../../../store/warningsEventForm/selectors';
import type { Area } from '../../../store/warningsForm/types';
import { warningEventDialogType } from '../../../store/warningsForm/utils';

export interface WarningEventDrawing {
  selectedAreas: Area[];
  select: (drawing: DrawingListItem) => void;
  deselect: () => void;
  hover: (drawing: DrawingListItem) => void;
  hoverEnd: () => void;
}

// What the Area & Drawing Loader does differently when opened from the warning
// event dialog instead of the public warning form
export const useWarningEventDrawing = (
  isLoaderOpen: boolean,
): WarningEventDrawing | undefined => {
  const dispatch = useDispatch();
  const isWarningEventDialogOpen = useSelector((store: CoreAppStore) =>
    uiSelectors.getisDialogOpen(store, warningEventDialogType),
  );
  const selectedDrawing = useSelector(getWarningsEventSelectedDrawing);
  const selectedDrawingId = selectedDrawing?.id;

  React.useEffect(() => {
    if (isLoaderOpen || !isWarningEventDialogOpen) {
      return;
    }
    dispatch(
      warningsEventFormActions.setHoveredDrawing({ drawing: undefined }),
    );
  }, [isLoaderOpen, isWarningEventDialogOpen, dispatch]);

  return React.useMemo(() => {
    if (!isWarningEventDialogOpen) {
      return undefined;
    }

    return {
      selectedAreas: selectedDrawing
        ? [
            {
              geoJSON: selectedDrawing.geoJSON,
              objectName: selectedDrawing.objectName,
            },
          ]
        : [],
      select: (drawing: DrawingListItem): void => {
        dispatch(warningsEventFormActions.setSelectedDrawing({ drawing }));
      },
      deselect: (): void => {
        dispatch(
          warningsEventFormActions.setSelectedDrawing({ drawing: undefined }),
        );
      },
      hover: (drawing: DrawingListItem): void => {
        if (drawing.id === selectedDrawingId) {
          return;
        }
        dispatch(warningsEventFormActions.setHoveredDrawing({ drawing }));
      },
      hoverEnd: (): void => {
        dispatch(
          warningsEventFormActions.setHoveredDrawing({ drawing: undefined }),
        );
      },
    };
  }, [isWarningEventDialogOpen, selectedDrawing, selectedDrawingId, dispatch]);
};
